<template>
    <BaseTemplate app-name="Relatórios" page-title="">
        <template #content>
            <!-- Banner Message -->
            <BannerMessage 
                v-if="showBanner"
                :type="bannerType"
                :message="bannerMessage"
                @close="showBanner = false"
            />


            <!-- Cartão de abas + filtros -->
            <div class="bg-white dark:bg-gray-800 shadow-sm border border-gray-100 dark:border-gray-800">
                <!-- Abas e botão de filtro -->
                <div class="flex items-center justify-between p-1 bg-gray-100 dark:bg-gray-900">
                    <nav class="flex flex-1 space-x-1">
                        <button
                            @click="activeTab = 'kpis'"
                            :class="[
                                'flex-1 py-2 px-3 text-sm font-medium transition-all duration-300 flex items-center justify-center gap-2 focus:outline-none rounded-lg',
                                activeTab === 'kpis' 
                                    ? 'bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-md border border-blue-100 dark:border-blue-900' 
                                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800'
                            ]"
                        >
                            <LayoutDashboard class="w-4 h-4" />
                            <span>KPI's de Produtividade</span>
                        </button>
                        <button
                            @click="activeTab = 'reports'"
                            :class="[
                                'flex-1 py-2 px-3 text-sm font-medium transition-all duration-300 flex items-center justify-center gap-2 focus:outline-none rounded-lg',
                                activeTab === 'reports' 
                                    ? 'bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 shadow-md border border-blue-100 dark:border-blue-900' 
                                    : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800'
                            ]"
                        >
                            <FileBarChart2 class="w-4 h-4" />
                            <span>Relatórios Personalizados</span>
                        </button>
                    </nav>
                    <!-- Botão de filtro -->
                    <div class="relative ml-2">

                        <div v-if="showFilters" class="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-lg z-50 p-4">
                            <div class="mb-4">
                                <label class="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2 flex items-center gap-1">
                                    <Calendar class="w-4 h-4" />
                                    Período
                                </label>
                                <select v-model="selectedPeriod" class="w-full border-0 bg-gray-50 dark:bg-gray-800 rounded-xl px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:bg-white dark:focus:bg-gray-900 transition-all duration-300 text-gray-800 dark:text-gray-100">
                                    <option value="today">Hoje</option>
                                    <option value="week">Esta Semana</option>
                                    <option value="month">Este Mês</option>
                                    <option value="quarter">Este Trimestre</option>
                                    <option value="year">Este Ano</option>
                                </select>
                            </div>
                            <div class="mb-4">
                                <label class="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2 flex items-center gap-1">
                                    <Tag class="w-4 h-4" />
                                    Categoria
                                </label>
                                <select v-model="selectedCategory" class="w-full border-0 bg-gray-50 dark:bg-gray-800 rounded-xl px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:bg-white dark:focus:bg-gray-900 transition-all duration-300 text-gray-800 dark:text-gray-100">
                                    <option value="">Todas</option>
                                    <option value="agendamento">Agendamento</option>
                                    <option value="laudos">Laudos</option>
                                    <option value="pacientes">Pacientes</option>
                                    <option value="medicos">Médicos</option>
                                    <option value="consultas">Consultas</option>
                                    <option value="modalidades">Modalidades</option>
                                    <option value="produtividade">Produtividade</option>
                                </select>
                            </div>
                            <button
                                @click="refreshData; showFilters = false"
                                class="clinic-btn w-full py-2 rounded-xl flex items-center justify-center gap-2 transition-all duration-300 hover:scale-105 transform shadow-lg"
                            >
                                <RefreshCw class="w-4 h-4" :class="{ 'animate-spin': isLoading }" />
                                Atualizar
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Conteúdo das abas -->
            <KPIsTab
                v-show="activeTab === 'kpis'"
                :agendas="agendas"
                :selected-period="selectedPeriod"
                :selected-category="selectedCategory"
            />

            <div v-show="activeTab === 'reports'" class="space-y-6 px-6">
                <!-- Formulário de emissão de relatório -->
                <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-8">
                    <h3 class="text-lg font-bold text-gray-900 dark:text-gray-100 mb-6">Emitir Relatório</h3>
                    <form @submit.prevent="fetchReport" class="space-y-6">
                        <!-- Configuração básica do relatório -->
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label class="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">Tipo de Relatório</label>
                                <select v-model="reportForm.type" @change="clearReportOnTypeChange" required class="w-full border-0 bg-gray-50 dark:bg-gray-900 rounded-xl px-4 py-2 focus:ring-2 focus:ring-blue-500 text-gray-800 dark:text-gray-100">
                                    <option value="">Selecione...</option>
                                    <option value="produtividade_medico">Produtividade de Médico</option>
                                    <option value="pacientes">Pacientes</option>
                                    <option value="agenda">Agenda</option>
                                    <option value="laudos">Laudos</option>
                                    <option value="consultas">Consultas</option>
                                    <option value="auditoria">Auditoria</option>
                                </select>
                            </div>
                            <div v-if="reportForm.type === 'agenda'">
                                <label class="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">Agenda</label>
                                <select v-model="reportForm.agenda" class="w-full border-0 bg-gray-50 dark:bg-gray-900 rounded-xl px-4 py-2 focus:ring-2 focus:ring-blue-500 text-gray-800 dark:text-gray-100">
                                    <option value="">Todas as Agendas</option>
                                    <option v-for="agenda in agendas" :key="agenda.id" :value="agenda.id">{{ agenda.name }}</option>
                                </select>
                            </div>
                            <div v-if="reportForm.type === 'produtividade_medico'">
                                <label class="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">Médico</label>
                                <select v-model="reportForm.medico" class="w-full border-0 bg-gray-50 dark:bg-gray-900 rounded-xl px-4 py-2 focus:ring-2 focus:ring-blue-500 text-gray-800 dark:text-gray-100">
                                    <option value="">Todos</option>
                                    <option v-for="med in medicos" :key="med.id" :value="med.id">{{ med.name }}</option>
                                </select>
                            </div>
                            <div v-if="reportForm.type === 'pacientes'">
                                <label class="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">Status do Paciente</label>
                                <select v-model="reportForm.status_paciente" class="w-full border-0 bg-gray-50 dark:bg-gray-900 rounded-xl px-4 py-2 focus:ring-2 focus:ring-blue-500 text-gray-800 dark:text-gray-100">
                                    <option value="">Todos</option>
                                    <option value="ativo">Ativo</option>
                                    <option value="inativo">Inativo</option>
                                </select>
                            </div>
                            <div v-if="reportForm.type === 'auditoria'">
                                <label class="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">Recurso</label>
                                <select v-model="reportForm.recurso" class="w-full border-0 bg-gray-50 dark:bg-gray-900 rounded-xl px-4 py-2 focus:ring-2 focus:ring-blue-500 text-gray-800 dark:text-gray-100">
                                    <option value="">Todos os Recursos</option>
                                    <option value="servico">Serviço</option>
                                    <option value="ativo">Ativo</option>
                                    <option value="nota">Nota</option>
                                    <option value="kit">Kit</option>
                                    <option value="movimentacao">Movimentação</option>
                                    <option value="manutencao">Manutenção</option>
                                    <option value="schedule">Agenda</option>
                                    <option value="appointment">Agendamento</option>
                                    <option value="sisreg_import">Importação SISREG</option>
                                    <option value="document">Documento</option>
                                    <option value="worklist">Worklist</option>
                                    <option value="anamnesis">Anamnese</option>
                                    <option value="editexam">Edição de Exame PACS</option>
                                    <option value="not_performed">Não Realizado</option>
                                    <option value="notificacao">Notificação</option>
                                </select>
                            </div>
                            <div v-if="reportForm.type === 'auditoria'">
                                <label class="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">Ação</label>
                                <select v-model="reportForm.acao" class="w-full border-0 bg-gray-50 dark:bg-gray-900 rounded-xl px-4 py-2 focus:ring-2 focus:ring-blue-500 text-gray-800 dark:text-gray-100">
                                    <option value="">Todas as Ações</option>
                                    <option value="create">Criar</option>
                                    <option value="update">Atualizar</option>
                                    <option value="delete">Excluir</option>
                                    <option value="view">Visualizar</option>
                                    <option value="export">Exportar</option>
                                    <option value="import">Importar</option>
                                    <option value="login">Login</option>
                                    <option value="logout">Logout</option>
                                    <option value="aceite termo">Aceite de Termo</option>
                                    <option value="recusa termo">Recusa de Termo</option>
                                    <option value="reconvocou paciente">Reconvocação de Paciente</option>
                                </select>
                            </div>
                        </div>

                        <!-- Filtros de período e status -->
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <!-- Filtro de período para todos os tipos de relatório -->
                            <div>
                                <label class="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">Período</label>
                                <div class="flex items-center gap-2">
                                    <input type="date" v-model="reportForm.data_inicio" class="flex-1 border-0 bg-gray-50 dark:bg-gray-900 rounded-xl px-4 py-2 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:bg-white dark:focus:bg-gray-800" />
                                    <span class="text-gray-500 dark:text-gray-400">a</span>
                                    <input type="date" v-model="reportForm.data_fim" class="flex-1 border-0 bg-gray-50 dark:bg-gray-900 rounded-xl px-4 py-2 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:bg-white dark:focus:bg-gray-800" />
                                </div>
                            </div>
                            <!-- Filtro de status para tipos específicos -->
                            <div v-if="reportForm.type === 'agenda'">
                                <label class="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">Status</label>
                                <select v-model="reportForm.status" class="w-full border-0 bg-gray-50 dark:bg-gray-900 rounded-xl px-4 py-2 focus:ring-2 focus:ring-blue-500 text-gray-800 dark:text-gray-100">
                                    <option value="">Todos os Status</option>
                                    <option value="REALIZADO">Realizado</option>
                                    <option value="LAUDADO">Laudado</option>
                                    <option value="FALTA">Falta</option>
                                    <option value="REAGENDADO">Reagendado</option>
                                    <option value="NÃO REALIZADO">Não Realizado</option>
                                </select>
                            </div>
                            <div v-if="reportForm.type === 'laudos' || reportForm.type === 'consultas'">
                                <label class="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">Status</label>
                                <select v-model="reportForm.status" class="w-full border-0 bg-gray-50 dark:bg-gray-900 rounded-xl px-4 py-2 focus:ring-2 focus:ring-blue-500 text-gray-800 dark:text-gray-100">
                                    <option value="">Todos os Status</option>
                                    <option value="pending">Pendente</option>
                                    <option value="laudado">Laudado</option>
                                    <option value="revisado">Revisado</option>
                                    <option value="finalized">Finalizado</option>
                                </select>
                            </div>
                            <div v-if="reportForm.type === 'produtividade_medico'">
                                <label class="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">Status do Laudo</label>
                                <select v-model="reportForm.status" class="w-full border-0 bg-gray-50 dark:bg-gray-900 rounded-xl px-4 py-2 focus:ring-2 focus:ring-blue-500 text-gray-800 dark:text-gray-100">
                                    <option value="">Todos os Status</option>
                                    <option value="laudado">Laudado</option>
                                    <option value="revisado">Revisado</option>
                                    <option value="pending">Pendente</option>
                                </select>
                            </div>
                        </div>

                        <!-- Configurações de formato -->
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label class="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">Tipo de Emissão</label>
                                <div class="flex gap-4">
                                    <label class="flex items-center gap-2 cursor-pointer">
                                        <input type="radio" v-model="reportForm.tipo_emissao" value="analitico" class="w-4 h-4 clinic-radio" />
                                        <span class="text-sm text-gray-700 dark:text-gray-200">Analítico</span>
                                    </label>
                                    <label class="flex items-center gap-2 cursor-pointer">
                                        <input type="radio" v-model="reportForm.tipo_emissao" value="sintetico" class="w-4 h-4 clinic-radio" />
                                        <span class="text-sm text-gray-700 dark:text-gray-200">Sintético</span>
                                    </label>
                                </div>
                            </div>
                            <div>
                                <label class="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">Orientação do PDF</label>
                                <div class="flex gap-4">
                                    <label class="flex items-center gap-2 cursor-pointer">
                                        <input type="radio" v-model="reportForm.orientation" value="portrait" class="w-4 h-4 clinic-radio" />
                                        <span class="text-sm text-gray-700 dark:text-gray-200">Vertical</span>
                                    </label>
                                    <label class="flex items-center gap-2 cursor-pointer">
                                        <input type="radio" v-model="reportForm.orientation" value="landscape" class="w-4 h-4 clinic-radio" />
                                        <span class="text-sm text-gray-700 dark:text-gray-200">Horizontal</span>
                                    </label>
                                </div>
                            </div>
                        </div>

                        <!-- Botão de ação -->
                        <div class="flex gap-3 justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
                            <button type="submit" class="clinic-btn px-6 py-2 rounded-xl font-semibold transition-all duration-300 text-sm flex items-center gap-2">
                                <Eye class="w-4 h-4" />
                                Gerar Relatório
                            </button>
                        </div>
                    </form>
                    <div v-if="reportData" class="mt-8 bg-gray-50 dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                        <div class="flex items-center justify-between mb-4">
                            <h3 class="font-bold text-gray-900 dark:text-gray-100">Relatório Gerado</h3>
                            <div class="flex gap-2">
                                <button 
                                    @click="previewPDF" 
                                    class="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 text-sm"
                                    :disabled="isPdfLoading"
                                >
                                    <RefreshCw v-if="isPdfLoading" class="w-4 h-4 animate-spin" />
                                    <FileText v-else class="w-4 h-4" />
                                    Visualizar PDF
                                </button>
                                <button 
                                    @click="downloadPDF" 
                                    class="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200 text-sm"
                                    :disabled="isPdfLoading"
                                >
                                    <RefreshCw v-if="isPdfLoading" class="w-4 h-4 animate-spin" />
                                    <Download v-else class="w-4 h-4" />
                                    Baixar PDF
                                </button>
                            </div>
                        </div>
                        
                        <!-- Resumo do relatório -->
                        <div class="grid grid-cols-1 md:grid-cols-5 gap-4">
                            <div class="bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                                <div class="text-sm text-gray-600 dark:text-gray-400">Tipo</div>
                                <div class="font-semibold text-gray-900 dark:text-gray-100">{{ getReportTypeName(reportForm.type) }}</div>
                            </div>
                            <div class="bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                                <div class="text-sm text-gray-600 dark:text-gray-400">Emissão</div>
                                <div class="font-semibold text-gray-900 dark:text-gray-100">{{ reportForm.tipo_emissao === 'analitico' ? 'Analítico' : 'Sintético' }}</div>
                            </div>
                            <div class="bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                                <div class="text-sm text-gray-600 dark:text-gray-400">Orientação</div>
                                <div class="font-semibold text-gray-900 dark:text-gray-100">{{ reportForm.orientation === 'portrait' ? 'Vertical' : 'Horizontal' }}</div>
                            </div>
                            <div v-if="reportForm.type !== 'auditoria'" class="bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                                <div class="text-sm text-gray-600 dark:text-gray-400">Status</div>
                                <div class="font-semibold text-gray-900 dark:text-gray-100">{{ getStatusName(reportForm.status) }}</div>
                            </div>
                            <div class="bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                                <div class="text-sm text-gray-600 dark:text-gray-400">Registros</div>
                                <div class="font-semibold text-gray-900 dark:text-gray-100">{{ reportData.data ? reportData.data.length : 0 }}</div>
                            </div>
                        </div>
                        
                        <!-- Campos específicos para auditoria -->
                        <div v-if="reportForm.type === 'auditoria'" class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                            <div class="bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                                <div class="text-sm text-gray-600 dark:text-gray-400">Recurso</div>
                                <div class="font-semibold text-gray-900 dark:text-gray-100">{{ getResourceName(reportForm.recurso) }}</div>
                            </div>
                            <div class="bg-white dark:bg-gray-800 p-3 rounded-lg border border-gray-200 dark:border-gray-700">
                                <div class="text-sm text-gray-600 dark:text-gray-400">Ação</div>
                                <div class="font-semibold text-gray-900 dark:text-gray-100">{{ getActionName(reportForm.acao) }}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Modais -->
            <!-- Removido MetricBuilderModal e KPIModal -->
        </template>
    </BaseTemplate>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import BaseTemplate from '../templates/BaseTemplate.vue'
import KPIsTab from './components/KPIsTab.vue'
import reportsService from '../../services/reportsService'
import { useClinicStore } from '../../stores/settings/clinicStore'
import { LayoutDashboard, FileBarChart2, Calendar, Tag, RefreshCw, Eye, Download, FileText } from 'lucide-vue-next'
import BannerMessage from '@/components/BannerMessage.vue'

// Store da clínica
const clinicStore = useClinicStore()

// Estado reativo
const selectedPeriod = ref('month')
const selectedCategory = ref('')

const activeTab = ref('kpis')
const isLoading = ref(false)
const showFilters = ref(false)

// Novo estado para emissão de relatório
const reportForm = ref({
    type: '',
    medico: '',
    agenda: '',
    status_paciente: '',
    status: '',
    data_inicio: '',
    data_fim: '',
    tipo_emissao: 'analitico',
    orientation: 'portrait',
    // Campos específicos para auditoria
    recurso: '',
    acao: '',
})
const medicos = ref([])
const agendas = ref([])
const reportData = ref(null)
const isPdfLoading = ref(false)

// Estado para banners
const showBanner = ref(false)
const bannerType = ref('success')
const bannerMessage = ref('')

// Função para exibir banners
const showBannerMessage = (type, message) => {
    bannerType.value = type
    bannerMessage.value = message
    showBanner.value = true
}

// Métodos
const loadData = async () => {
    isLoading.value = true
    try {
        const [medicosData, agendasData] = await Promise.all([
            loadMedicos(),
            loadAgendas()
        ])
        medicos.value = medicosData
        agendas.value = agendasData
    } catch (error) {
        console.error('Erro ao carregar dados:', error)
        showBannerMessage('error', 'Erro ao carregar dados. Tente novamente.')
        // Em caso de erro, mostrar mensagem ao usuário
        medicos.value = []
        agendas.value = []
    } finally {
        isLoading.value = false
    }
}

const loadMedicos = async () => {
    try {
        // Buscar médicos via API do Schedules
        const response = await fetch('/api/v1/schedules/doctors/list/', {
            credentials: 'include'
        })
        if (response.ok) {
            return await response.json()
        }
        return []
    } catch (error) {
        console.error('Erro ao carregar médicos:', error)
        return []
    }
}

const loadAgendas = async () => {
    try {
        // Buscar agendas via API do Schedules
        const response = await fetch('/api/v1/schedules/list/?page_size=100', {
            credentials: 'include'
        })
        if (response.ok) {
            const data = await response.json()
            return data.results || []
        }
        return []
    } catch (error) {
        console.error('Erro ao carregar agendas:', error)
        return []
    }
}

const refreshData = () => {
    loadData()
}



const fetchReport = async () => {
    try {
        isLoading.value = true
        
        // Preparar dados do formulário
        const reportRequestData = {
            type: reportForm.value.type,
            filters: {
                medico: reportForm.value.medico || null,
                agenda: reportForm.value.agenda || null,
                status_paciente: reportForm.value.status_paciente || null,
                status: reportForm.value.status || null,
                data_inicio: reportForm.value.data_inicio || null,
                data_fim: reportForm.value.data_fim || null,
                // Filtros específicos para auditoria
                resource: reportForm.value.recurso || null,
                action: reportForm.value.acao || null,
            },
            tipo_emissao: reportForm.value.tipo_emissao,
            orientation: reportForm.value.orientation,
            clinic_logo: clinicStore.clinic.logo // Adicionar logo da clínica
        }
        
        // Chamar API para gerar relatório
        const response = await reportsService.generateCustomReport(reportRequestData)
        reportData.value = response
        showBannerMessage('success', 'Relatório gerado com sucesso!')
        
    } catch (error) {
        console.error('Erro ao gerar relatório:', error)
        showBannerMessage('error', 'Erro ao gerar relatório. Tente novamente.')
    } finally {
        isLoading.value = false
    }
}

// Função para limpar relatório quando tipo for alterado
const clearReport = () => {
    reportData.value = null
}

// Limpar relatório quando tipo for alterado
const clearReportOnTypeChange = () => {
    if (reportForm.value.type) {
        clearReport()
    }
}

const previewPDF = async () => {
    try {
        isPdfLoading.value = true
        
        // Preparar dados do formulário
        const reportRequestData = {
            type: reportForm.value.type,
            filters: {
                medico: reportForm.value.medico || null,
                agenda: reportForm.value.agenda || null,
                status_paciente: reportForm.value.status_paciente || null,
                status: reportForm.value.status || null,
                data_inicio: reportForm.value.data_inicio || null,
                data_fim: reportForm.value.data_fim || null,
                // Filtros específicos para auditoria
                resource: reportForm.value.recurso || null,
                action: reportForm.value.acao || null,
            },
            tipo_emissao: reportForm.value.tipo_emissao,
            orientation: reportForm.value.orientation,
            clinic_logo: clinicStore.clinic.logo // Adicionar logo da clínica
        }
        
        // Chamar API para gerar preview do PDF
        const response = await reportsService.previewCustomReportPDF(reportRequestData)
        
        // Abrir PDF em nova aba
        const blob = new Blob([response], { type: 'application/pdf' })
        const url = window.URL.createObjectURL(blob)
        window.open(url, '_blank')
        
        showBannerMessage('success', 'PDF visualizado com sucesso!')
        
    } catch (error) {
        console.error('Erro ao visualizar PDF:', error)
        showBannerMessage('error', 'Erro ao gerar preview do PDF. Tente novamente.')
    } finally {
        isPdfLoading.value = false
    }
}

const downloadPDF = async () => {
    try {
        isPdfLoading.value = true
        
        // Preparar dados do formulário
        const reportRequestData = {
            type: reportForm.value.type,
            filters: {
                medico: reportForm.value.medico || null,
                agenda: reportForm.value.agenda || null,
                status_paciente: reportForm.value.status_paciente || null,
                status: reportForm.value.status || null,
                data_inicio: reportForm.value.data_inicio || null,
                data_fim: reportForm.value.data_fim || null,
                // Filtros específicos para auditoria
                resource: reportForm.value.recurso || null,
                action: reportForm.value.acao || null,
            },
            tipo_emissao: reportForm.value.tipo_emissao,
            orientation: reportForm.value.orientation,
            clinic_logo: clinicStore.clinic.logo // Adicionar logo da clínica
        }
        
        // Chamar API para gerar e baixar PDF
        const response = await reportsService.downloadCustomReportPDF(reportRequestData)
        
        // Criar link para download
        const blob = new Blob([response], { type: 'application/pdf' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `relatorio_${reportForm.value.type}_${new Date().toISOString().split('T')[0]}.pdf`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        
        showBannerMessage('success', 'PDF baixado com sucesso!')
        
    } catch (error) {
        console.error('Erro ao baixar PDF:', error)
        showBannerMessage('error', 'Erro ao gerar PDF. Tente novamente.')
    } finally {
        isPdfLoading.value = false
    }
}



// Computed properties - removidas pois foram movidas para o componente KPIsTab

const getReportTypeName = (type) => {
    const typeNames = {
        'produtividade_medico': 'Produtividade de Médico',
        'pacientes': 'Pacientes',
        'agenda': 'Agenda',
        'laudos': 'Laudos',
        'consultas': 'Consultas',
        'auditoria': 'Auditoria'
    }
    return typeNames[type] || type
}

const getStatusName = (status) => {
    if (!status) return 'Todos'
    
    const statusNames = {
        'pending': 'Pendente',
        'confirmed': 'Confirmado',
        'cancelled': 'Cancelado',
        'completed': 'Concluído',
        'rescheduled': 'Reagendado',
        'no_show': 'Falta',
        'waiting': 'Aguardando',
        'in_progress': 'Em Atendimento',
        'finalized': 'Finalizado',
        'attended': 'Atendido',
        'not_attended': 'Não Atendido',
        'not_performed': 'Não Realizado',
        'not_realized': 'Não Realizado',
        'NÃO REALIZADO': 'Não Realizado',
        'laudado': 'Laudado'
    }
    return statusNames[status] || status
}

const getResourceName = (resource) => {
    if (!resource) return 'Todos os Recursos'
    const resourceNames = {
        'servico': 'Serviço',
        'ativo': 'Ativo',
        'nota': 'Nota',
        'kit': 'Kit',
        'movimentacao': 'Movimentação',
        'manutencao': 'Manutenção',
        'schedule': 'Agenda',
        'appointment': 'Agendamento',
        'sisreg_import': 'Importação SISREG',
        'document': 'Documento',
        'worklist': 'Worklist',
        'anamnesis': 'Anamnese',
        'editexam': 'Edição de Exame PACS',
        'not_performed': 'Não Realizado',
        'notificacao': 'Notificação'
    }
    return resourceNames[resource] || resource
}

const getActionName = (action) => {
    if (!action) return 'Todas as Ações'
    const actionNames = {
        'create': 'Criar',
        'update': 'Atualizar',
        'delete': 'Excluir',
        'view': 'Visualizar',
        'export': 'Exportar',
        'import': 'Importar',
        'login': 'Login',
        'logout': 'Logout',
        'aceite termo': 'Aceite de Termo',
        'recusa termo': 'Recusa de Termo',
        'reconvocou paciente': 'Reconvocação de Paciente'
    }
    return actionNames[action] || action
}

// Lifecycle
onMounted(() => {
    // Inicializar store da clínica
    clinicStore.initialize()
    loadData()
})
</script>

<style scoped>
/* Estilos para radio buttons personalizados */
.clinic-radio {
    accent-color: var(--clinic-btn-bg) !important;
}
.clinic-radio:checked {
    accent-color: var(--clinic-btn-bg) !important;
}
</style>
